// ---------------------------------------------------------------------------
//  QuoteWizard.jsx  – wizard principal
// ---------------------------------------------------------------------------
import React, { useState } from "react";
import {
  Box,
  Paper,
  Stepper,
  Step,
  StepLabel,
  Typography,
  useMediaQuery,
  useTheme,
  StepConnector,
  stepConnectorClasses,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { motion, AnimatePresence } from "framer-motion";

import StepLeadForm from "../components/quote/StepLeadForm";
import StepBillUpload from "../components/quote/StepBillUpload";
import StepBillReview from "../components/quote/StepBillReview";
import StepQuotePreview from "../components/quote/StepQuotePreview";

/* ───────────────── Stepper en color corporativo ───────────────── */
const ColorConnector = styled(StepConnector)(() => ({
  [`& .${stepConnectorClasses.line}`]: { borderColor: "#e06a00" },
}));
function ColorStepIcon({ active, completed, className, icon }) {
  return (
    <Box
      className={className}
      sx={{
        width: 32,
        height: 32,
        border: "2px solid #e06a00",
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontWeight: 700,
        bgcolor: completed
          ? "#e06a00"
          : active
          ? "rgba(224,106,0,.08)"
          : "#fff",
        color: completed ? "#fff" : "#e06a00",
        transition: ".3s",
      }}
    >
      {icon}
    </Box>
  );
}

/* ──────────────────────────── Componente ──────────────────────────── */
export default function QuoteWizard() {
  /* paso activo */
  const [active, setActive] = useState(0);

  /* estado global de los datos */
  const [state, setState] = useState({
    lead: null, // { name, phone, email }
    billFiles: [], // Array<File>
    billData: null, // resultados del parse + revisión
  });

  /* helpers de MUI */
  const theme = useTheme();
  const downSm = useMediaQuery(theme.breakpoints.down("sm"));
  const ToolbarSpacer = <Box sx={theme.mixins.toolbar} />;

  /* --------------------------------------------------- Pasos */
  const steps = [
    /* ① Datos de contacto */
    {
      label: "Datos",
      content: (
        <StepLeadForm
          defaultValues={state.lead}
          onComplete={(lead) => {
            setState((s) => ({ ...s, lead }));
            setActive(1);
          }}
        />
      ),
    },

    /* ② Cargar recibo */
    {
      label: "Recibo",
      content: (
        <StepBillUpload
          onComplete={(billFiles, billData) => {
            setState((s) => ({ ...s, billFiles, billData }));
            setActive(2);
          }}
          onBack={() => setActive(0)}
        />
      ),
    },

    /* ③ Revisión editable */
    {
      label: "Revisión",
      content: (
        <StepBillReview
          defaultData={state.billData}
          onComplete={(billData) => {
            setState((s) => ({ ...s, billData })); // aplicar ediciones
            setActive(3);
          }}
          onBack={() => setActive(1)}
        />
      ),
    },

    /* ④ Vista previa / PDF */
    {
      label: "PDF",
      content: (
        <StepQuotePreview
          leadData={state.lead} // nombre / tel / email
          quoteData={state.billData} // resultados numéricos completos
          billFiles={state.billFiles} // (por si los requieres luego)
          onBack={() => setActive(2)}
          onRestart={() => {
            setActive(0);
            setState({ lead: null, billFiles: [], billData: null });
          }}
          onSendEmail={(payload) => {
            /* TODO: integrar backend de correo */
            console.log("📧 Enviando por correo:", payload);
          }}
        />
      ),
    },
  ];

  /* --------------------------------------------------- Render */
  return (
    <Box>
      {ToolbarSpacer}

      <Box
        sx={{
          pt: { xs: 10, md: 14 },
          pb: { xs: 10, md: 14 },
          px: { xs: 2, md: 6 },
        }}
      >
        <Typography variant="h4" align="center" fontWeight={900} mb={5}>
          Cotiza tu sistema solar en minutos
        </Typography>

        <Paper
          component={motion.div}
          initial={{ opacity: 0, y: 60 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          sx={{
            p: { xs: 2, md: 5 },
            maxWidth: 900,
            mx: "auto",
            borderRadius: 5,
            boxShadow: "0 12px 40px rgba(0,0,0,.12)",
          }}
        >
          <Stepper
            activeStep={active}
            orientation={downSm ? "vertical" : "horizontal"}
            connector={<ColorConnector />}
            alternativeLabel={!downSm}
            sx={{ mb: 4 }}
          >
            {steps.map((s, i) => (
              <Step key={s.label} completed={active > i}>
                <StepLabel StepIconComponent={ColorStepIcon}>
                  {s.label}
                </StepLabel>
              </Step>
            ))}
          </Stepper>

          <AnimatePresence mode="wait">
            <Box
              key={active}
              component={motion.div}
              initial={{ opacity: 0, x: 60 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -60 }}
              transition={{ duration: 0.4 }}
            >
              {steps[active].content}
            </Box>
          </AnimatePresence>
        </Paper>
      </Box>
    </Box>
  );
}
